const CANNON_ATOM_LIMIT = 4500; //Works at the limit for aerolysin-DNA. Next limit is 1000 (protein-DNA complex)
const OPENMM_ATOM_LIMIT = 4500; //Works at the limit for aerolysin-DNA and for Glivec example.  Next limit = 2200 (TEM lactamase)
const ANI_ATOM_LIMIT = 300; //Works for biggest systems
const CALVADOS_ATOM_LIMIT = 6000; //Works for "Cyt c oxidase"
const VIEWONLY_ATOM_LIMIT = 20000; //Works for "ATPase"

export function validatePdb(pdb, engine) {
  //Check string is not empty
  if (pdb.trim() === "") {
    return { isValid: false, msg: "Paste valid PDB coordinates or choose an example." };
  }

  //Check number of atoms is within limits
  const lines = pdb.split("\n");

  let atomCount = 0;
  let atomCountLimitFactor = 0.5;
  if (pdb.toLowerCase().includes("luciano")) {
    atomCountLimitFactor = 1;            //Will apply to the intensive simulation engines only (amber, calvados, cannon)
  }
  if (pdb.toLowerCase().includes("lucianoluciano")) {
    atomCountLimitFactor = 2;            //Will apply to the intensive simulation engines only (amber, calvados, cannon)
  }
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (line.startsWith("ATOM") || line.startsWith("HETATM")) {
      atomCount++;
      if (atomCount > OPENMM_ATOM_LIMIT*atomCountLimitFactor && engine === "OPENMM") {
        return { isValid: false, msg: "Too many atoms for Amber simulation; maximum is " + OPENMM_ATOM_LIMIT*atomCountLimitFactor + "." };
      }
      if (atomCount > VIEWONLY_ATOM_LIMIT && engine === "VIEWONLY") {
        return { isValid: false, msg: "Too many atoms; maximum for visualization-only HandMol is " + VIEWONLY_ATOM_LIMIT + "." };
      }
      if (atomCount > ANI_ATOM_LIMIT && engine === "ANI") {
        return { isValid: false, msg: "Too many atoms for ANI simulation; maximum is " + ANI_ATOM_LIMIT + "." };
      }
      if (atomCount > CANNON_ATOM_LIMIT*atomCountLimitFactor && engine === "CANNON") {
        return { isValid: false, msg: "Too many atoms for CANNON simulation; maximum is " + CANNON_ATOM_LIMIT*atomCountLimitFactor + "." };
      }
      if (atomCount > CALVADOS_ATOM_LIMIT*atomCountLimitFactor && engine === "CALVADOS") {
        return { isValid: false, msg: "Too many atoms for CALVADOS simulation; maximum is " + CALVADOS_ATOM_LIMIT*atomCountLimitFactor + "." };
      }
    }
  }

  //Check number of atoms is > 0
  if (atomCount == 0) {
    return { isValid: false, msg: "No atoms in valid ATOM or HETATM records." };
  }

  //Check that all elements are fine for ANI
  if (engine === "ANI") {
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line.startsWith("ATOM") || line.startsWith("HETATM")) {
        const element = lines[i].substring(76, 79).trim().replace("+", "").replace("-", "").trim().toUpperCase();
        if (element === "") {
          return {
            isValid: false,
            msg:
              "For ANI simulations it is mandatory to indicate element symbols in the last column. Please check the examples, and keep in mind that only H, C, N, O, F, Cl and S are supported.\nPDB line: " +
              lines[i],
          };
        }
        if (
          !(
            element === "C" ||
            element === "H" ||
            element === "N" ||
            element === "O" ||
            element === "S" ||
            element === "F" ||
            element === "CL"
          )
        ) {
          return {
            isValid: false,
            msg:
              lines[i].substring(76, 79).trim().replace("+", "").replace("-", "") +
              " is not a valid atom for ANI simulations, which only support H, C, N, O, F, Cl and S.\nPDB line: " +
              lines[i],
          };
        }
      }
    }
  }

  //Check that all residues are fine for CALVADOS
  if (engine === "OPENMM") {
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line.startsWith("ATOM") || line.startsWith("HETATM")) {
        const thisresname = lines[i].substring(17, 20).trim();
        if (
          !(
            thisresname === "ALA" ||
            thisresname === "ARG" ||
            thisresname === "ASH" ||
            thisresname === "ASN" ||
            thisresname === "ASP" ||
            thisresname === "CYM" ||
            thisresname === "CYS" ||
            thisresname === "CYX" ||
            thisresname === "GLH" ||
            thisresname === "GLN" ||
            thisresname === "GLU" ||
            thisresname === "GLY" ||
            thisresname === "HID" ||
            thisresname === "HIS" ||
            thisresname === "HIE" ||
            thisresname === "HIP" ||
            thisresname === "HYP" ||
            thisresname === "ILE" ||
            thisresname === "LEU" ||
            thisresname === "LYN" ||
            thisresname === "LYS" ||
            thisresname === "MET" ||
            thisresname === "PHE" ||
            thisresname === "PRO" ||
            thisresname === "SER" ||
            thisresname === "THR" ||
            thisresname === "TRP" ||
            thisresname === "TYR" ||
            thisresname === "VAL" ||
            thisresname === "CALA" ||
            thisresname === "CARG" ||
            thisresname === "CASN" ||
            thisresname === "CASP" ||
            thisresname === "CCYS" ||
            thisresname === "CCYX" ||
            thisresname === "CGLN" ||
            thisresname === "CGLU" ||
            thisresname === "CGLY" ||
            thisresname === "CHID" ||
            thisresname === "CHIE" ||
            thisresname === "CHIP" ||
            thisresname === "CHYP" ||
            thisresname === "CILE" ||
            thisresname === "CLEU" ||
            thisresname === "CLYS" ||
            thisresname === "CMET" ||
            thisresname === "CPHE" ||
            thisresname === "CPRO" ||
            thisresname === "CSER" ||
            thisresname === "CTHR" ||
            thisresname === "CTRP" ||
            thisresname === "CTYR" ||
            thisresname === "CVAL" ||
            thisresname === "NHE" ||
            thisresname === "NME" ||
            thisresname === "ACE" ||
            thisresname === "NALA" ||
            thisresname === "NARG" ||
            thisresname === "NASN" ||
            thisresname === "NASP" ||
            thisresname === "NCYS" ||
            thisresname === "NCYX" ||
            thisresname === "NGLN" ||
            thisresname === "NGLU" ||
            thisresname === "NGLY" ||
            thisresname === "NHID" ||
            thisresname === "NHIE" ||
            thisresname === "NHIP" ||
            thisresname === "NILE" ||
            thisresname === "NLEU" ||
            thisresname === "NLYS" ||
            thisresname === "NMET" ||
            thisresname === "NPHE" ||
            thisresname === "NPRO" ||
            thisresname === "NSER" ||
            thisresname === "NTHR" ||
            thisresname === "NTRP" ||
            thisresname === "NTYR" ||
            thisresname === "NVAL" ||
            
            thisresname === "DA" ||
            thisresname === "DA3" ||
            thisresname === "DA5" ||
            thisresname === "DAN" ||
            thisresname === "DC" ||
            thisresname === "DC3" ||
            thisresname === "DC5" ||
            thisresname === "DCN" ||
            thisresname === "DG" ||
            thisresname === "DG3" ||
            thisresname === "DG5" ||
            thisresname === "DGN" ||
            thisresname === "DT" ||
            thisresname === "DT3" ||
            thisresname === "DT5" ||
            thisresname === "DTN" ||

            thisresname === "GUA" ||
            thisresname === "CYT" ||
            thisresname === "URA" ||
            thisresname === "ADE" ||

            thisresname === "A" ||
            thisresname === "A3" ||
            thisresname === "A5" ||
            thisresname === "AN" ||
            thisresname === "C" ||
            thisresname === "C3" ||
            thisresname === "C5" ||
            thisresname === "CN" ||
            thisresname === "G" ||
            thisresname === "G3" ||
            thisresname === "G5" ||
            thisresname === "GN" ||
            thisresname === "OHE" ||
            thisresname === "U" ||
            thisresname === "U3" ||
            thisresname === "U5" ||
            thisresname === "UN" ||

            thisresname === "HOH"
          )
        ) {
          return {
            isValid: false,
            msg: "Residue type not allowed for AMBER simulations. PDB line:\n " + lines[i] + "\n\nConsider creating parm7+rst files on your end, for example with CHARMM-GUI, and uploading them directly.",
          };
        }
      }
    }
  }

  //Check that all residues are fine for CALVADOS
  if (engine === "CALVADOS") {
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line.startsWith("ATOM") || line.startsWith("HETATM")) {
        const thisresname = lines[i].substring(17, 20).trim();
        if (
          !(
            thisresname === "ALA" ||
            thisresname === "CYS" ||
            thisresname === "ASP" ||
            thisresname === "GLU" ||
            thisresname === "PHE" ||
            thisresname === "GLY" ||
            thisresname === "HIS" ||
            thisresname === "ILE" ||
            thisresname === "LEU" ||
            thisresname === "LYS" ||
            thisresname === "MET" ||
            thisresname === "ASN" ||
            thisresname === "PRO" ||
            thisresname === "GLN" ||
            thisresname === "ARG" ||
            thisresname === "SER" ||
            thisresname === "THR" ||
            thisresname === "VAL" ||
            thisresname === "TRP" ||
            thisresname === "TYR"
          )
        ) {
          return {
            isValid: false,
            msg: "For CALVADOS simulations only proteins containing the 20 amino acids are allowed.\nPDB line: " + lines[i],
          };
        }
      }
    }
  }

  //Check that all numbers are good numbers; also line length
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.startsWith("ATOM") || line.startsWith("HETATM")) {
      //Check atom number is fine
      if (isNaN(line.substring(7, 12).trim())) {
        return { isValid: false, msg: "Non numeric value for atom number at the following line:\n" + line };
      }
      //Check residue number is fine
      if (isNaN(line.substring(22, 26).trim())) {
        return { isValid: false, msg: "Non numeric value for residue number at the following line:\n" + line };
      }
      //Check x, y and z coordinates are fine
      if (
        isNaN(line.substring(30, 38).trim()) ||
        isNaN(line.substring(38, 46).trim()) ||
        isNaN(line.substring(46, 54).trim())
      ) {
        return { isValid: false, msg: "Non numeric value for atomic coordinates at the following line:\n" + line };
      }
      //Check that line isn't too short (limit of 67 works for OpenMM, not for ANI and CANNON)
      if (line.length < 67) {
        //OpenMM check
        return { isValid: false, msg: "Line with atom data seems too short:\n" + line };
      } else {
        if (line.length < 76 && engine === "CANNON") {
          //CANNON check   //note that for ANI this is checked earlier when element symbols are checked for
          return { isValid: false, msg: "Line with atom data seems too short:\n" + line };
        }
      }
    }
  }

  return { isValid: true, msg: "Valid PDB" };
}
